import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  GraduationCap, BookOpen, Award, Clock, CheckCircle2,
  Circle, Play, ChevronRight, ChevronLeft, Trophy, Star,
  FileText, Target, Shield, Moon, Brain, ClipboardCheck,
  RefreshCw, Download, Printer, AlertCircle, Check, X
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

// Category icons
const categoryIcons = {
  sms_fundamentals: Shield,
  incident_reporting: FileText,
  risk_management: Target,
  fatigue_management: Moon,
  audit_management: ClipboardCheck,
  ai_tools: Brain,
};

// Status badges
const statusConfig = {
  not_started: { color: 'bg-gray-100 text-gray-800', label: 'Not Started' },
  in_progress: { color: 'bg-blue-100 text-blue-800', label: 'In Progress' },
  completed: { color: 'bg-green-100 text-green-800', label: 'Completed' },
};

export default function TrainingModulesPage() {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('modules');
  const [loading, setLoading] = useState(false);
  
  // Data
  const [modules, setModules] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  
  // Active module/lesson
  const [activeModule, setActiveModule] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [lessonIndex, setLessonIndex] = useState(0);
  
  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  
  // Certificate viewer
  const [showCertificate, setShowCertificate] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Fetch modules
  const fetchModules = async () => {
    try {
      const res = await fetch(`${API_URL}/api/training-modules/modules`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setModules(data.modules || []);
      }
    } catch (err) {
      console.error('Error fetching modules:', err);
    }
  };

  // Fetch dashboard
  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/training-modules/dashboard`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
        // Build progress map
        const progressMap = {};
        for (const p of data.progress || []) {
          progressMap[p.module_code] = p;
        }
        setUserProgress(progressMap);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  };

  // Fetch certificates
  const fetchCertificates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/training-modules/certificates`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setCertificates(data.certificates || []);
      }
    } catch (err) {
      console.error('Error fetching certificates:', err);
    }
  };

  // Seed modules
  const seedModules = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/training-modules/seed`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        await fetchAll();
      }
    } catch (err) {
      toast.error('Error seeding modules');
    } finally {
      setLoading(false);
    }
  };

  // Start module
  const startModule = async (moduleCode) => {
    try {
      const res = await fetch(`${API_URL}/api/training-modules/modules/${moduleCode}/start`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        toast.success('Module started');
        await fetchDashboard();
      }
    } catch (err) {
      toast.error('Error starting module');
    }
  };

  // Complete lesson
  const completeLesson = async (moduleCode, lessonId) => {
    try {
      const res = await fetch(`${API_URL}/api/training-modules/modules/${moduleCode}/lessons/${lessonId}/complete`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        await fetchDashboard();
      }
    } catch (err) {
      console.error('Error completing lesson:', err);
    }
  };

  // Submit quiz
  const submitQuiz = async () => {
    if (!activeModule) return;
    
    setLoading(true);
    try {
      const answers = Object.entries(quizAnswers).map(([questionId, answer]) => ({
        question_id: questionId,
        answer: answer
      }));
      
      const res = await fetch(`${API_URL}/api/training-modules/modules/${activeModule.code}/quiz`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify(answers)
      });
      
      if (res.ok) {
        const data = await res.json();
        setQuizResult(data);
        if (data.passed) {
          toast.success(`Congratulations! You passed with ${data.score.toFixed(0)}%`);
          await fetchAll();
        } else {
          toast.error(`Score: ${data.score.toFixed(0)}%. You need ${data.passing_score}% to pass.`);
        }
      }
    } catch (err) {
      toast.error('Error submitting quiz');
    } finally {
      setLoading(false);
    }
  };

  // Open module
  const openModule = async (module) => {
    setActiveModule(module);
    setLessonIndex(0);
    setActiveLesson(module.lessons?.[0] || null);
    setShowQuiz(false);
    setQuizAnswers({});
    setQuizResult(null);
    
    // Start module if not started
    const progress = userProgress[module.code];
    if (!progress || progress.status === 'not_started') {
      await startModule(module.code);
    }
    
    setActiveTab('learn');
  };

  // Navigate lessons
  const goToNextLesson = async () => {
    if (!activeModule) return;
    
    // Mark current lesson complete
    if (activeLesson) {
      await completeLesson(activeModule.code, activeLesson.id);
    }
    
    const lessons = activeModule.lessons || [];
    if (lessonIndex < lessons.length - 1) {
      setLessonIndex(lessonIndex + 1);
      setActiveLesson(lessons[lessonIndex + 1]);
    } else {
      // All lessons done, show quiz
      setShowQuiz(true);
    }
  };

  const goToPrevLesson = () => {
    if (!activeModule) return;
    
    if (showQuiz) {
      setShowQuiz(false);
      const lessons = activeModule.lessons || [];
      setLessonIndex(lessons.length - 1);
      setActiveLesson(lessons[lessons.length - 1]);
    } else if (lessonIndex > 0) {
      setLessonIndex(lessonIndex - 1);
      setActiveLesson(activeModule.lessons[lessonIndex - 1]);
    }
  };

  // Fetch all
  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchModules(),
      fetchDashboard(),
      fetchCertificates()
    ]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, [token]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // Get module progress
  const getModuleProgress = (module) => {
    const progress = userProgress[module.code];
    if (!progress) return { status: 'not_started', percent: 0 };
    
    const totalLessons = module.lessons?.length || 1;
    const completedLessons = progress.lessons_completed?.length || 0;
    const percent = (completedLessons / totalLessons) * 100;
    
    return {
      status: progress.status,
      percent: progress.status === 'completed' ? 100 : percent,
      passed: progress.passed,
      score: progress.quiz_score
    };
  };

  // Render lesson content with markdown-like formatting
  const renderLessonContent = (content) => {
    if (!content) return null;
    
    return content.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return <h2 key={i} className="text-xl font-bold mt-6 mb-3">{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={i} className="text-lg font-semibold mt-4 mb-2">{line.replace('### ', '')}</h3>;
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-bold my-1">{line.replace(/\*\*/g, '')}</p>;
      }
      if (line.startsWith('- ')) {
        return (
          <div key={i} className="flex items-start gap-2 ml-4 my-1">
            <ChevronRight className="h-4 w-4 text-blue-500 mt-1 flex-shrink-0" />
            <span>{line.replace('- ', '')}</span>
          </div>
        );
      }
      if (line.startsWith('✓ ') || line.startsWith('✗ ')) {
        const isCheck = line.startsWith('✓');
        return (
          <div key={i} className={`flex items-start gap-2 ml-4 my-1 ${isCheck ? 'text-green-600' : 'text-red-600'}`}>
            {isCheck ? <Check className="h-4 w-4 mt-1" /> : <X className="h-4 w-4 mt-1" />}
            <span>{line.substring(2)}</span>
          </div>
        );
      }
      if (line.match(/^\d+\. /)) {
        const num = line.match(/^\d+/)[0];
        return (
          <div key={i} className="flex items-start gap-2 ml-4 my-1">
            <span className="font-medium text-blue-600 min-w-[20px]">{num}.</span>
            <span>{line.replace(/^\d+\. /, '')}</span>
          </div>
        );
      }
      if (line.trim() === '') {
        return <div key={i} className="h-2" />;
      }
      return <p key={i} className="my-1">{line}</p>;
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="training-modules-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <GraduationCap className="h-7 w-7 text-indigo-600" />
            {t('training.modules')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('training.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchAll} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
          <Button onClick={seedModules} disabled={loading} data-testid="seed-modules-btn">
            <BookOpen className="h-4 w-4 mr-2" />
            {t('common.loading')} {t('training.modules')}
          </Button>
        </div>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Modules</p>
                  <p className="text-2xl font-bold">{dashboard.total_modules}</p>
                </div>
                <BookOpen className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{dashboard.completed}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600">{dashboard.in_progress}</p>
                </div>
                <Play className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Certificates</p>
                  <p className="text-2xl font-bold text-yellow-600">{dashboard.certificates}</p>
                </div>
                <Award className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Completion</p>
                  <p className="text-2xl font-bold">{dashboard.completion_rate}%</p>
                </div>
                <Trophy className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="modules" data-testid="tab-modules">
            <BookOpen className="h-4 w-4 mr-2" />
            Modules
          </TabsTrigger>
          <TabsTrigger value="learn" data-testid="tab-learn" disabled={!activeModule}>
            <Play className="h-4 w-4 mr-2" />
            Learn
          </TabsTrigger>
          <TabsTrigger value="certificates" data-testid="tab-certificates">
            <Award className="h-4 w-4 mr-2" />
            Certificates
          </TabsTrigger>
        </TabsList>

        {/* Modules Tab */}
        <TabsContent value="modules" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {modules.map(module => {
              const progress = getModuleProgress(module);
              const CategoryIcon = categoryIcons[module.category] || BookOpen;
              
              return (
                <Card 
                  key={module.id || module.code} 
                  className={`cursor-pointer hover:shadow-md transition-shadow ${progress.status === 'completed' ? 'border-green-500' : ''}`}
                  onClick={() => openModule(module)}
                  data-testid={`module-card-${module.code}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`p-3 rounded-lg ${progress.status === 'completed' ? 'bg-green-100' : 'bg-blue-100'}`}>
                          <CategoryIcon className={`h-6 w-6 ${progress.status === 'completed' ? 'text-green-600' : 'text-blue-600'}`} />
                        </div>
                        <div>
                          <Badge variant="outline">{module.code}</Badge>
                          {module.is_required && <Badge className="ml-2 bg-red-100 text-red-800">Required</Badge>}
                        </div>
                      </div>
                      {progress.status === 'completed' && (
                        <CheckCircle2 className="h-6 w-6 text-green-500" />
                      )}
                    </div>
                    
                    <h3 className="font-semibold text-lg mb-1">{module.title}</h3>
                    {module.title_ar && <p className="text-sm text-gray-500 mb-2" dir="rtl">{module.title_ar}</p>}
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{module.description}</p>
                    
                    <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {module.duration_minutes} min
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="h-4 w-4" />
                        {module.lessons?.length || 0} lessons
                      </span>
                      <span className="flex items-center gap-1">
                        <Target className="h-4 w-4" />
                        {module.passing_score}% to pass
                      </span>
                    </div>
                    
                    <div className="mt-4">
                      <div className="flex justify-between text-sm mb-1">
                        <Badge className={statusConfig[progress.status]?.color}>
                          {statusConfig[progress.status]?.label}
                        </Badge>
                        <span>{progress.percent.toFixed(0)}%</span>
                      </div>
                      <Progress value={progress.percent} className="h-2" />
                    </div>
                    
                    {progress.passed && (
                      <div className="mt-3 flex items-center gap-2 text-green-600">
                        <Trophy className="h-4 w-4" />
                        <span className="text-sm font-medium">Passed with {progress.score?.toFixed(0)}%</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {modules.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No training modules found.</p>
                <p className="text-sm text-gray-400 mt-1">Click "Load Modules" to populate training content.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Learn Tab */}
        <TabsContent value="learn" className="space-y-4">
          {activeModule && (
            <>
              {/* Module header */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge variant="outline" className="mb-2">{activeModule.code}</Badge>
                      <h2 className="text-xl font-bold">{activeModule.title}</h2>
                      <p className="text-sm text-gray-500 mt-1">{activeModule.description}</p>
                    </div>
                    <Button variant="outline" onClick={() => setActiveTab('modules')}>
                      Back to Modules
                    </Button>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span>
                        {showQuiz ? 'Quiz' : `Lesson ${lessonIndex + 1} of ${activeModule.lessons?.length || 0}`}
                      </span>
                      <span>{getModuleProgress(activeModule).percent.toFixed(0)}% complete</span>
                    </div>
                    <Progress value={getModuleProgress(activeModule).percent} className="h-2" />
                  </div>
                </CardContent>
              </Card>

              {/* Lesson or Quiz content */}
              {!showQuiz ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-blue-500" />
                      {activeLesson?.title}
                    </CardTitle>
                    <CardDescription>
                      <Clock className="h-4 w-4 inline mr-1" />
                      {activeLesson?.duration_minutes || 10} minutes
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="prose prose-sm dark:prose-invert max-w-none">
                        {renderLessonContent(activeLesson?.content)}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-purple-500" />
                      Knowledge Check
                    </CardTitle>
                    <CardDescription>
                      Answer the following questions. You need {activeModule.passing_score}% to pass.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px] pr-4">
                      <div className="space-y-6">
                        {(activeModule.quiz_questions || []).map((q, qIndex) => (
                          <div key={q.id} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <p className="font-medium mb-3">
                              {qIndex + 1}. {q.question}
                            </p>
                            
                            {q.type === 'multiple_choice' && (
                              <RadioGroup
                                value={quizAnswers[q.id] || ''}
                                onValueChange={(value) => setQuizAnswers({ ...quizAnswers, [q.id]: value })}
                              >
                                {q.options?.map((option, oIndex) => (
                                  <div key={oIndex} className="flex items-center space-x-2">
                                    <RadioGroupItem value={option} id={`${q.id}-${oIndex}`} />
                                    <Label htmlFor={`${q.id}-${oIndex}`}>{option}</Label>
                                  </div>
                                ))}
                              </RadioGroup>
                            )}
                            
                            {q.type === 'true_false' && (
                              <RadioGroup
                                value={quizAnswers[q.id]?.toString() || ''}
                                onValueChange={(value) => setQuizAnswers({ ...quizAnswers, [q.id]: value === 'true' })}
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="true" id={`${q.id}-true`} />
                                  <Label htmlFor={`${q.id}-true`}>True</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem value="false" id={`${q.id}-false`} />
                                  <Label htmlFor={`${q.id}-false`}>False</Label>
                                </div>
                              </RadioGroup>
                            )}
                            
                            {q.type === 'multi_select' && (
                              <div className="space-y-2">
                                {q.options?.map((option, oIndex) => {
                                  const selected = quizAnswers[q.id] || [];
                                  return (
                                    <div key={oIndex} className="flex items-center space-x-2">
                                      <Checkbox
                                        id={`${q.id}-${oIndex}`}
                                        checked={selected.includes(option)}
                                        onCheckedChange={(checked) => {
                                          const current = quizAnswers[q.id] || [];
                                          if (checked) {
                                            setQuizAnswers({ ...quizAnswers, [q.id]: [...current, option] });
                                          } else {
                                            setQuizAnswers({ ...quizAnswers, [q.id]: current.filter(o => o !== option) });
                                          }
                                        }}
                                      />
                                      <Label htmlFor={`${q.id}-${oIndex}`}>{option}</Label>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Show result if quiz was submitted */}
                            {quizResult && (
                              <div className={`mt-3 p-2 rounded ${
                                quizResult.graded_answers?.find(a => a.question_id === q.id)?.correct 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {quizResult.graded_answers?.find(a => a.question_id === q.id)?.correct ? (
                                  <span className="flex items-center gap-1"><Check className="h-4 w-4" /> Correct</span>
                                ) : (
                                  <span className="flex items-center gap-1"><X className="h-4 w-4" /> Incorrect</span>
                                )}
                                <p className="text-sm mt-1">{q.explanation}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}

              {/* Navigation buttons */}
              <div className="flex justify-between">
                <Button variant="outline" onClick={goToPrevLesson} disabled={lessonIndex === 0 && !showQuiz}>
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>
                
                {showQuiz ? (
                  <Button 
                    onClick={submitQuiz} 
                    disabled={loading || Object.keys(quizAnswers).length < (activeModule.quiz_questions?.length || 0)}
                    data-testid="submit-quiz-btn"
                  >
                    {loading ? 'Submitting...' : 'Submit Quiz'}
                    <Check className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button onClick={goToNextLesson} data-testid="next-lesson-btn">
                    {lessonIndex === (activeModule.lessons?.length || 0) - 1 ? 'Go to Quiz' : 'Next Lesson'}
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>

              {/* Quiz Result */}
              {quizResult && (
                <Card className={quizResult.passed ? 'border-green-500' : 'border-red-500'}>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      {quizResult.passed ? (
                        <>
                          <Trophy className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
                          <h3 className="text-2xl font-bold text-green-600">Congratulations!</h3>
                          <p className="text-gray-600 mt-2">
                            You passed with {quizResult.score.toFixed(0)}% ({quizResult.correct}/{quizResult.total} correct)
                          </p>
                          <Button className="mt-4" onClick={() => setActiveTab('certificates')}>
                            <Award className="h-4 w-4 mr-2" />
                            View Certificate
                          </Button>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-16 w-16 mx-auto text-red-500 mb-4" />
                          <h3 className="text-2xl font-bold text-red-600">Not Passed</h3>
                          <p className="text-gray-600 mt-2">
                            Score: {quizResult.score.toFixed(0)}% ({quizResult.correct}/{quizResult.total} correct)
                          </p>
                          <p className="text-sm text-gray-500">You need {quizResult.passing_score}% to pass. Review the lessons and try again.</p>
                          <Button className="mt-4" variant="outline" onClick={() => {
                            setQuizAnswers({});
                            setQuizResult(null);
                          }}>
                            Try Again
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {!activeModule && (
            <Card>
              <CardContent className="py-12 text-center">
                <Play className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">Select a module to start learning</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Certificates Tab */}
        <TabsContent value="certificates" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {certificates.map(cert => (
              <Card 
                key={cert.id} 
                className="cursor-pointer hover:shadow-md transition-shadow border-yellow-500"
                onClick={() => {
                  setSelectedCertificate(cert);
                  setShowCertificate(true);
                }}
                data-testid={`certificate-${cert.certificate_number}`}
              >
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Award className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
                    <Badge className="bg-yellow-100 text-yellow-800 mb-2">{cert.module_code}</Badge>
                    <h3 className="font-semibold">{cert.module_title}</h3>
                    <p className="text-sm text-gray-500 mt-2">Score: {cert.score.toFixed(0)}%</p>
                    <p className="text-xs text-gray-400 mt-1">Issued: {formatDate(cert.issued_at)}</p>
                    {cert.expires_at && (
                      <p className="text-xs text-gray-400">Expires: {formatDate(cert.expires_at)}</p>
                    )}
                    <p className="text-xs font-mono mt-2 text-gray-500">{cert.certificate_number}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {certificates.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Award className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No certificates yet.</p>
                <p className="text-sm text-gray-400 mt-1">Complete training modules to earn certificates.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Certificate Dialog */}
      <Dialog open={showCertificate} onOpenChange={setShowCertificate}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Training Certificate</DialogTitle>
          </DialogHeader>
          {selectedCertificate && (
            <div className="p-8 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 rounded-lg border-4 border-yellow-500">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <Award className="h-20 w-20 text-yellow-500" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  Certificate of Completion
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">This certifies that</p>
                <h3 className="text-2xl font-bold text-indigo-600 mb-6">
                  {selectedCertificate.user_name}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-2">has successfully completed</p>
                <h4 className="text-xl font-semibold mb-2">{selectedCertificate.module_title}</h4>
                <Badge className="bg-blue-100 text-blue-800">{selectedCertificate.module_code}</Badge>
                
                <div className="mt-6 flex justify-center gap-8">
                  <div>
                    <p className="text-sm text-gray-500">Score</p>
                    <p className="text-xl font-bold text-green-600">{selectedCertificate.score.toFixed(0)}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Issued</p>
                    <p className="font-medium">{formatDate(selectedCertificate.issued_at)}</p>
                  </div>
                  {selectedCertificate.expires_at && (
                    <div>
                      <p className="text-sm text-gray-500">Valid Until</p>
                      <p className="font-medium">{formatDate(selectedCertificate.expires_at)}</p>
                    </div>
                  )}
                </div>
                
                <Separator className="my-6" />
                
                <p className="text-xs text-gray-500 font-mono">
                  Certificate Number: {selectedCertificate.certificate_number}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCertificate(false)}>
              Close
            </Button>
            <Button>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
